// Reusable Trophy Claim address-collection modal.
//
// Mounted from two places:
//   • StudentBadgesPage — user clicks "Claim — ship it to me!" on a card.
//   • TrophyClaimGate  — auto-pop the moment a pending unclaimed trophy
//                        is detected (so students never miss a plaque).
//
// `trophy` is the meta dict from /student/badges/trophies (id, name, icon,
// color, desc) or from /trophies/pending-claim (already merged with meta).
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from './studentApi';
import { Sparkles, MapPin, Phone, X } from 'lucide-react';
import { notifyError } from '../../lib/errors';

export default function TrophyClaimModal({ trophy, onClose, onClaimed }) {
  const [form, setForm] = useState({
    full_name: '', address_line1: '', address_line2: '',
    city: '', state: '', postal_code: '', country: 'India', phone: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.full_name || !form.address_line1 || !form.city || !form.postal_code || !form.phone) {
      toast.error('Please fill all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/student/badges/trophies/${trophy.id}/claim`, form);
      toast.success(`${trophy.name} is on its way.${res.email_sent_to ? ` Confirmation sent to ${res.email_sent_to}` : ''}`);
      onClaimed?.();
    } catch (e) {
      notifyError(e, 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const color = trophy.color || '#34A853';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="claim-trophy-modal">
      <div className="rounded-3xl shadow-lg border-2 max-w-lg w-full max-h-[90vh] overflow-auto relative"
        style={{ background: 'var(--st-card)', borderColor: color }}>
        {/* Close (X) */}
        <button onClick={onClose}
          data-testid="claim-trophy-close"
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-black/5 z-10"
          aria-label="Close">
          <X className="w-4 h-4 opacity-60" />
        </button>

        {/* Header */}
        <div className="p-6 sm:p-7 pb-3"
          style={{ background: `linear-gradient(135deg, ${color}22, transparent)` }}>
          <div className="flex items-center gap-3">
            <div className="text-5xl">{trophy.icon}</div>
            <div className="min-w-0">
              <div className="text-xs font-semibold " style={{ color }}>
                You unlocked a trophy
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {trophy.name}
              </h3>
              {trophy.desc && <p className="text-xs opacity-70 mt-1">{trophy.desc}</p>}
            </div>
          </div>
          <p className="text-xs opacity-80 mt-4 leading-relaxed">
            We'll ship this <b>physical Deskey plaque</b> straight to your home —
            <b style={{ color }}> shipping is on us</b>. Confirm your delivery
            address below and we'll email you tracking the moment it dispatches.
          </p>
        </div>

        {/* Form */}
        <div className="p-6 sm:p-7 pt-4 space-y-3">
          <Field label="Full name *" icon={<Sparkles className="w-3 h-3" />}>
            <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
              placeholder="As it should appear on the parcel" data-testid="claim-name" />
          </Field>
          <Field label="Phone *" icon={<Phone className="w-3 h-3" />}>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              placeholder="Courier will call before delivery" data-testid="claim-phone" />
          </Field>
          <Field label="Address line 1 *" icon={<MapPin className="w-3 h-3" />}>
            <Input value={form.address_line1} onChange={e => setForm({ ...form, address_line1: e.target.value })}
              placeholder="Street, house / flat number" data-testid="claim-addr1" />
          </Field>
          <Field label="Address line 2">
            <Input value={form.address_line2} onChange={e => setForm({ ...form, address_line2: e.target.value })}
              placeholder="Landmark / area (optional)" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City *">
              <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                placeholder="City" data-testid="claim-city" />
            </Field>
            <Field label="State / Region">
              <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
                placeholder="State" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Postal code *">
              <Input value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })}
                placeholder="PIN / ZIP" data-testid="claim-postal" />
            </Field>
            <Field label="Country">
              <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                placeholder="Country" />
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-7 pb-6 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full text-xs" data-testid="claim-remind-later">
            Remind me later
          </Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="claim-submit"
            className="rounded-full text-xs font-semibold px-5"
            style={{ background: color, color: '#000' }}>
            {submitting ? 'Sending…' : 'Confirm and ship'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold opacity-60 mb-1 flex items-center gap-1">
        {icon} {label}
      </span>
      {children}
    </label>
  );
}
